// client/src/components/CitationsDashboard.js
// Rebuilt Jun 2026 — Content Health / Visibility styling
// Tiles + prompt table + right-hand drawer per prompt showing engine responses + citations
// AUG 18 2026: Added competitor citation rate toggle to prompts table
import React, { useState, useEffect } from 'react';

if (typeof document !== 'undefined') {
  if (!document.querySelector('link[href*="tabler-icons"]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/tabler-icons.min.css';
    document.head.appendChild(link);
  }
  if (!document.querySelector('link[href*="Work+Sans"]')) {
    const fl = document.createElement('link');
    fl.href = 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700;800&display=swap';
    fl.rel = 'stylesheet';
    document.head.appendChild(fl);
  }
}

const colors = {
  primary: '#39393a',
  secondary: '#048ba8',
  tertiary: '#e9c46a',
  accent: '#f4a261',
  grey: '#e76f51',
  green: '#01796f'
};

const ENGINE_COLORS = {
  chatgpt:    '#10a37f',
  claude:     '#cc785c',
  gemini:     '#4285f4',
  perplexity: '#1fb8cd'
};

const ENGINE_LABELS = {
  chatgpt: 'ChatGPT', claude: 'Claude', gemini: 'Gemini', perplexity: 'Perplexity'
};

function band(score) {
  if (score === null || score === undefined) return { bg: '#f3f4f6', text: '#9ca3af', bar: '#d1d5db' };
  if (score >= 70) return { bg: 'rgba(1,121,111,0.08)', text: '#01796f', bar: '#01796f' };
  if (score >= 45) return { bg: 'rgba(233,196,106,0.15)', text: '#854F0B', bar: '#e9c46a' };
  return { bg: 'rgba(231,111,81,0.08)', text: '#993C1D', bar: '#e76f51' };
}

function ScoreCell({ score }) {
  const b = band(score);
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px', padding: '3px 8px', borderRadius: '6px', backgroundColor: b.bg, color: b.text, fontSize: '13px', fontWeight: '600' }}>
      {score === null || score === undefined ? '—' : `${score}%`}
    </div>
  );
}

function extractDomain(url) {
  try { return new URL(url).hostname.replace('www.', '').toLowerCase(); }
  catch { return url; }
}

function renderMarkdown(text) {
  if (!text) return 'No response text available.';
  let html = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/\n/g, '<br />');
  return html;
}

// ---- CSV Download Helper ----
function downloadCSV(filename, rows) {
  const csv = rows.map(r => r.map(cell => {
    const str = String(cell || '');
    return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str;
  }).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// ---- Drawer ----
function Drawer({ open, onClose, title, badge, badgeBg, badgeColor, children }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000 }} />
      <div role="dialog" style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(600px, 92vw)', backgroundColor: 'white', boxShadow: '-8px 0 24px rgba(0,0,0,0.18)', zIndex: 1001, display: 'flex', flexDirection: 'column', fontFamily: "'Work Sans', sans-serif" }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '15px', fontWeight: '700', color: colors.primary, lineHeight: 1.4 }}>{title}</span>
              {badge && <span style={{ padding: '3px 10px', borderRadius: '8px', backgroundColor: badgeBg, color: badgeColor, fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap' }}>{badge}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1, flexShrink: 0 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
          {children}
        </div>
      </div>
    </>
  );
}

// ---- Prompt drawer body ----
function PromptDrawerBody({ result, brandDomains }) {
  const engines = ['chatgpt', 'claude', 'gemini', 'perplexity'];
  const responses = result.responses || [];
  return (
    <>
      {engines.map(engine => {
        const resp = responses.find(r => r.engine === engine && !r.error);
        if (!resp) return null;
        const color = ENGINE_COLORS[engine];
        const label = ENGINE_LABELS[engine];
        const citations = resp.citations || resp.sources?.domains || [];
        const isBrandCited = citations.some(url => {
          const u = typeof url === 'string' ? url : (url?.url || url?.source || '');
          return brandDomains.some(d => u.toLowerCase().includes(d));
        });
        return (
          <div key={engine} style={{ marginBottom: '16px', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            {/* Engine header */}
            <div style={{ padding: '10px 14px', backgroundColor: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color }} />
                <span style={{ fontSize: '13px', fontWeight: '700', color: colors.primary }}>{label}</span>
                {resp.model && <span style={{ fontSize: '11px', color: '#9ca3af' }}>{resp.model}</span>}
              </div>
              {isBrandCited && (
                <span style={{ fontSize: '11px', fontWeight: '600', color: colors.green, backgroundColor: 'rgba(1,121,111,0.08)', padding: '2px 8px', borderRadius: '4px' }}>✓ Your site cited</span>
              )}
            </div>
            {/* Response text */}
            <div style={{ padding: '14px', backgroundColor: 'white' }}>
              <div style={{ fontSize: '13px', lineHeight: '1.6', color: '#374151' }}
                dangerouslySetInnerHTML={{ __html: renderMarkdown(resp.response) }} />
            </div>
            {/* Citations */}
            {citations.length > 0 && (
              <div style={{ padding: '10px 14px', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  <i className="ti ti-paperclip" style={{ marginRight: '4px' }} />
                  {citations.length} source{citations.length !== 1 ? 's' : ''} cited
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {citations.map((url, i) => {
                    const urlStr = typeof url === 'string' ? url : (url?.url || url?.source || '');
                    const domain = extractDomain(urlStr);
                    const isBrand = brandDomains.some(d => urlStr.toLowerCase().includes(d));
                    return (
                      <a key={i} href={urlStr.startsWith('http') ? urlStr : `https://${urlStr}`}
                        target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: '12px', padding: '3px 10px', borderRadius: '6px', textDecoration: 'none', backgroundColor: isBrand ? 'rgba(1,121,111,0.08)' : `${color}12`, color: isBrand ? colors.green : color, border: `1px solid ${isBrand ? colors.green : color}30`, fontWeight: isBrand ? '600' : '400' }}>
                        {domain}
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
            {citations.length === 0 && (
              <div style={{ padding: '10px 14px', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', fontSize: '12px', color: '#9ca3af' }}>
                No citations in this response
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

// ---- Main ----
export default function CitationsDashboard({ campaignId, userId, urls, apiBaseUrl }) {
  const [loading, setLoading] = useState(true);
  const [latestAudit, setLatestAudit] = useState(null);
  const [error, setError] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [showCompetitors, setShowCompetitors] = useState(false);

  const brandDomains = [...new Set((urls || []).map(u => {
    const urlStr = typeof u === 'string' ? u : u?.url || '';
    try { return new URL(urlStr).hostname.replace(/^www\./, '').toLowerCase(); }
    catch { return null; }
  }).filter(Boolean))];

  useEffect(() => { loadAuditData(); }, [campaignId]);

  const loadAuditData = async () => {
    if (!campaignId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/audit-results?campaignId=${campaignId}&moduleType=llm-audit&limit=1`);
      if (!res.ok) throw new Error('Failed to fetch audit results');
      const data = await res.json();
      if (data && data.length > 0) setLatestAudit(data[0]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div style={{ padding: '48px', textAlign: 'center', fontFamily: "'Work Sans', sans-serif" }}>
      <div style={{ width: '36px', height: '36px', border: '4px solid #e5e7eb', borderTop: `4px solid ${colors.secondary}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
      <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>Loading citation data...</p>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ padding: '16px', backgroundColor: '#fee2e2', borderRadius: '8px', fontFamily: "'Work Sans', sans-serif" }}>
      <span style={{ color: '#dc2626', fontSize: '14px' }}>{error}</span>
    </div>
  );

  if (!latestAudit) return (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '48px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', fontFamily: "'Work Sans', sans-serif" }}>
      <i className="ti ti-paperclip" style={{ fontSize: '40px', color: '#d1d5db', marginBottom: '12px', display: 'block' }} />
      <h3 style={{ fontSize: '18px', fontWeight: '600', color: colors.primary, marginBottom: '8px' }}>No citation data yet</h3>
      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Run an audit to see which sources AI engines cite.</p>
    </div>
  );

  const resultData = latestAudit.result_data || {};
  const results = resultData.results || [];
  const summary = resultData.summary || {};
  const citationSources = summary.citationSources || {};
  const targetBrandMetric = summary.brandMetrics?.find(b => b.isTarget);
  const competitorMetrics = (summary.brandMetrics || []).filter(b => !b.isTarget);
  const hasCanonical = typeof summary.citationRate === 'number' && targetBrandMetric;

  const totalResponses = results.reduce((sum, r) => sum + (r.responses || []).filter(resp => !resp.error).length, 0);
  let totalWithCitations = 0;
  results.forEach(pr => {
    totalWithCitations += (pr.responses || []).filter(r => !r.error && r.citations?.length > 0).length;
  });

  let citationRate, totalBrandCited;
  if (hasCanonical) {
    citationRate = summary.citationRate;
    totalBrandCited = targetBrandMetric.responsesWithBrandCitation || 0;
  } else {
    totalBrandCited = 0;
    results.forEach(pr => {
      (pr.responses || []).filter(r => !r.error && r.citations?.length > 0).forEach(r => {
        if (r.citations.some(c => {
          const u = typeof c === 'string' ? c : (c?.url || c?.source || '');
          return brandDomains.some(d => u.toLowerCase().includes(d));
        })) totalBrandCited++;
      });
    });
    citationRate = totalWithCitations > 0 ? Math.round((totalBrandCited / totalWithCitations) * 100) : 0;
  }

  // Calculate competitor citation rate per prompt
  const calculateCompetitorCitationRatePerPrompt = (prompt, responses) => {
    const competitorRates = {};
    competitorMetrics.forEach(comp => {
      const compResponses = (responses || []).filter(r => !r.error);
      if (compResponses.length === 0) {
        competitorRates[comp.brand] = null;
        return;
      }
      // Count responses with citations that mention competitor name
      const withCitations = compResponses.filter(r => r.citations?.length > 0);
      const citedCompetitor = withCitations.filter(r => {
        const respText = (r.response || '').toLowerCase();
        return respText.includes(comp.brand.toLowerCase());
      }).length;
      const rate = withCitations.length > 0 ? Math.round((citedCompetitor / withCitations.length) * 100) : null;
      competitorRates[comp.brand] = rate;
    });
    return competitorRates;
  };

  // Per-prompt citation rate
  const promptRows = results.map(pr => {
    const responses = (pr.responses || []).filter(r => !r.error);
    const withCitations = responses.filter(r => r.citations?.length > 0);
    const brandCited = withCitations.filter(r =>
      r.citations.some(c => {
        const u = typeof c === 'string' ? c : (c?.url || c?.source || '');
        return brandDomains.some(d => u.toLowerCase().includes(d));
      })
    ).length;
    const rate = withCitations.length > 0 ? Math.round((brandCited / withCitations.length) * 100) : null;
    const totalCitations = responses.reduce((s, r) => s + (r.citations?.length || 0), 0);
    const competitorRates = calculateCompetitorCitationRatePerPrompt(pr.prompt, responses);
    return { prompt: pr.prompt || pr.promptText || '', rate, totalCitations, result: pr, competitorRates };
  }).filter(r => r.prompt);

  // Download citations table to CSV
  const downloadCitationsTableCSV = () => {
    const today = new Date().toISOString().split('T')[0];
    const rows = [['Prompt', 'Citation Rate', 'Total Citations']];
    if (showCompetitors) {
      rows[0] = ['Prompt', 'Brand', 'Citation Rate', 'Total Citations'];
      promptRows.forEach(row => {
        rows.push([row.prompt, targetBrandMetric?.brand || 'Your brand', row.rate !== null ? `${row.rate}%` : '—', row.totalCitations.toString()]);
        competitorMetrics.forEach(comp => {
          rows.push([row.prompt, comp.brand, row.competitorRates[comp.brand] !== null ? `${row.competitorRates[comp.brand]}%` : '—', '']);
        });
      });
    } else {
      promptRows.forEach(row => {
        rows.push([row.prompt, row.rate !== null ? `${row.rate}%` : '—', row.totalCitations.toString()]);
      });
    }
    downloadCSV(`citations-by-prompt-${today}.csv`, rows);
  };

  // Header tiles
  const tiles = [
    { label: 'Citation rate', icon: 'ti-quote', value: citationRate, suffix: '%' },
    { label: 'Unique sources', icon: 'ti-world', value: citationSources.totalUniqueDomains || 0, raw: true },
    { label: 'Responses', icon: 'ti-message', value: totalResponses, raw: true },
    { label: 'Engines', icon: 'ti-cpu', value: 4, raw: true }
  ];

  return (
    <div style={{ fontFamily: "'Work Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '20px 24px', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', color: colors.primary, margin: 0, marginBottom: '2px' }}>Citations</h2>
        <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
          Sources cited by AI engines · {totalBrandCited} of {totalWithCitations} cited responses include your domain
        </p>
      </div>

      {/* Tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(110px, 160px))', gap: '10px', marginBottom: '16px', justifyContent: 'start' }}>
        {tiles.map(tile => {
          const b = tile.raw ? { bg: '#f9fafb', text: colors.primary, bar: colors.secondary } : band(tile.value);
          const display = tile.value === null || tile.value === undefined ? '—' : tile.raw ? tile.value : `${tile.value}%`;
          const pct = tile.raw ? null : tile.value;
          return (
            <div key={tile.label} style={{ backgroundColor: 'white', border: '0.5px solid #e5e7eb', borderRadius: '10px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                <i className={`ti ${tile.icon}`} style={{ fontSize: '15px', color: tile.value !== null ? b.text : '#9ca3af' }} />
                <span style={{ fontSize: '11px', color: '#6b7280' }}>{tile.label}</span>
              </div>
              <div style={{ fontSize: '26px', fontWeight: '700', color: b.text, marginBottom: '8px' }}>{display}</div>
              <div style={{ height: '4px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
                {pct !== null && <div style={{ width: pct + '%', height: '100%', background: b.bar, borderRadius: '2px' }} />}
              </div>
            </div>
          );
        })}
      </div>

      {/* Prompt table with competitor toggle */}
      {promptRows.length > 0 && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: colors.primary }}>Citation by prompt</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Click a row to see engine responses and cited sources</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
              {competitorMetrics.length > 0 && (
                <div style={{ display: 'flex', gap: '4px', backgroundColor: '#f3f4f6', padding: '2px', borderRadius: '6px' }}>
                  <button
                    onClick={() => setShowCompetitors(false)}
                    style={{
                      padding: '5px 10px',
                      borderRadius: '4px',
                      border: 'none',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      backgroundColor: !showCompetitors ? 'white' : 'transparent',
                      color: !showCompetitors ? colors.secondary : '#6b7280',
                      transition: 'all 0.2s'
                    }}
                    title="Show brand-only citation rate"
                  >
                    Brand only
                  </button>
                  <button
                    onClick={() => setShowCompetitors(true)}
                    style={{
                      padding: '5px 10px',
                      borderRadius: '4px',
                      border: 'none',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      backgroundColor: showCompetitors ? 'white' : 'transparent',
                      color: showCompetitors ? colors.secondary : '#6b7280',
                      transition: 'all 0.2s'
                    }}
                    title="Include competitors"
                  >
                    + Competitors
                  </button>
                </div>
              )}
              <button
                onClick={downloadCitationsTableCSV}
                title="Download as CSV"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '0',
                  fontSize: '12px',
                  color: '#6b7280',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.color = colors.secondary}
                onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
              >
                <i className="ti ti-download" style={{ fontSize: '14px' }} />
                <span>CSV</span>
              </button>
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '10px 20px', color: '#6b7280', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Prompt</th>
                <th style={{ textAlign: 'center', padding: '10px 16px', color: '#6b7280', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>
                  <i className="ti ti-quote" style={{ fontSize: '14px' }} title="Citation rate" />
                </th>
                <th style={{ textAlign: 'center', padding: '10px 16px', color: '#6b7280', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>
                  <i className="ti ti-paperclip" style={{ fontSize: '14px' }} title="Total citations" />
                </th>
              </tr>
            </thead>
            <tbody>
              {promptRows.map((row, idx) => (
                <React.Fragment key={idx}>
                  <tr
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer', backgroundColor: 'white' }}
                    onClick={() => setDrawer(row)}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}>
                    <td style={{ padding: '12px 20px', color: colors.primary, lineHeight: '1.4' }}>{row.prompt}</td>
                    <td style={{ textAlign: 'center', padding: '12px 16px' }}>
                      <ScoreCell score={row.rate} />
                    </td>
                    <td style={{ textAlign: 'center', padding: '12px 16px' }}>
                      <span style={{ fontSize: '13px', color: row.totalCitations > 0 ? colors.secondary : '#9ca3af', fontWeight: row.totalCitations > 0 ? '600' : '400' }}>
                        {row.totalCitations > 0 ? row.totalCitations : '—'}
                      </span>
                    </td>
                  </tr>
                  {showCompetitors && competitorMetrics.map(comp => (
                    <tr key={`${idx}-${comp.brand}`}
                      style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: '#fafafa', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fafafa'}>
                      <td style={{ padding: '8px 20px 8px 48px', color: '#6b7280', fontSize: '12px', lineHeight: '1.4' }}>
                        {comp.brand}
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px 16px' }}>
                        <ScoreCell score={row.competitorRates[comp.brand]} />
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px 16px' }}>
                        <span style={{ fontSize: '12px', color: '#9ca3af' }}>—</span>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {/* Legend */}
          <div style={{ padding: '10px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
            {[
              { label: '≥70', bg: 'rgba(1,121,111,0.08)', color: '#01796f' },
              { label: '45–69', bg: 'rgba(233,196,106,0.15)', color: '#854F0B' },
              { label: '<45', bg: 'rgba(231,111,81,0.08)', color: '#993C1D' }
            ].map(({ label, bg, color }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#6b7280' }}>
                <div style={{ width: '20px', height: '12px', borderRadius: '3px', backgroundColor: bg, border: `1px solid ${color}30` }} />
                <span>{label}</span>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#6b7280', marginLeft: '8px' }}>
              <i className="ti ti-quote" style={{ fontSize: '12px' }} /> Citation rate (your domain)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#6b7280' }}>
              <i className="ti ti-paperclip" style={{ fontSize: '12px' }} /> Total sources cited
            </div>
          </div>
        </div>
      )}

      {/* Drawer */}
      {drawer && (() => {
        const b = band(drawer.rate);
        return (
          <Drawer open={true} onClose={() => setDrawer(null)}
            title={drawer.prompt}
            badge={drawer.rate !== null ? `${drawer.rate}% citation rate` : 'No citations'}
            badgeBg={b.bg} badgeColor={b.text}>
            <PromptDrawerBody result={drawer.result} brandDomains={brandDomains} />
          </Drawer>
        );
      })()}
    </div>
  );
}
